import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { getMyAccountingAssignments } from "../../api/accounting";
import {
  changeMyPassword,
  getMyProfile,
  removeMyProfilePhoto,
  updateMyProfile,
  uploadMyProfilePhoto,
} from "../../api/profile";
import ImageUploadField from "../../components/ImageUploadField";
import ConfirmModal from "../../components/modals/ConfirmModal";
import { useAuth } from "../../context/AuthContext";
import { isSalonBusinessCpaAccountant } from "../../utils/accountingRoles";
import { accountingPath } from "./accountingRouteContext";
import SalonBusinessCpaShell from "./SalonBusinessCpaShell";

const DEFAULT_PROFILE = {
  user_id: null,
  role: "",
  full_name: "",
  email: "",
  username: "",
  photo_url: "",
};

function roleLabel(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "salonbusinesscpa_accountant") return "Ellyanna Accounting accountant";
  if (!normalized) return "User";
  return normalized
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function messageForError(message) {
  switch (message) {
    case "FULL_NAME_REQUIRED":
      return "Full name is required.";
    case "USERNAME_INVALID":
      return "Username can only use letters, numbers, underscores, or hyphens.";
    case "USERNAME_TAKEN":
      return "That username is already in use.";
    case "INVALID_CURRENT_PASSWORD":
      return "Current password is incorrect.";
    default:
      return message || "Something went wrong.";
  }
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(date);
}

function accessLabel(value) {
  return String(value || "").toLowerCase() === "read_only" ? "Read only" : "Review work";
}

function normalizeProfile(payload = {}) {
  return {
    user_id: Number(payload?.user_id || 0) || null,
    role: String(payload?.role || "").toLowerCase(),
    full_name: payload?.full_name || "",
    email: payload?.email || "",
    username: payload?.username || "",
    photo_url: payload?.photo_url || "",
  };
}

export default function SalonBusinessCpaMyProfile() {
  const navigate = useNavigate();
  const { token, user, logout, updateCurrentUser } = useAuth();
  const [profile, setProfile] = useState(DEFAULT_PROFILE);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(Boolean(token));
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState("");
  const [saving, setSaving] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  const isAccountant = isSalonBusinessCpaAccountant(profile.role || user?.role);

  const loadProfile = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setAssignmentsLoading(true);
    setSaveError("");
    try {
      const profileData = await getMyProfile(token);
      const normalized = normalizeProfile(profileData);
      setProfile(normalized);
      if (isSalonBusinessCpaAccountant(normalized.role || user?.role)) {
        const assignmentData = await getMyAccountingAssignments(token);
        setAssignments(Array.isArray(assignmentData?.assignments) ? assignmentData.assignments : []);
      } else {
        setAssignments([]);
      }
    } catch (err) {
      setSaveError(messageForError(err?.message || "Unable to load your profile."));
    } finally {
      setLoading(false);
      setAssignmentsLoading(false);
    }
  }, [token, user?.role]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const totals = useMemo(
    () =>
      assignments.reduce(
        (next, item) => ({
          open: next.open + Number(item.open_request_count || 0),
          assignedOpen:
            next.assignedOpen + Number(item.assigned_open_request_count || 0),
          received: next.received + Number(item.received_document_count || 0),
          reviewing: next.reviewing + Number(item.reviewing_document_count || 0),
        }),
        { open: 0, assignedOpen: 0, received: 0, reviewing: 0 }
      ),
    [assignments]
  );

  const handleFieldChange = (field, value) => {
    setSaveError("");
    setSaveSuccess("");
    setProfile((current) => ({ ...current, [field]: value }));
  };

  const handleSaveProfile = async (event) => {
    event.preventDefault();
    if (!token) return;
    setSaving(true);
    setSaveError("");
    setSaveSuccess("");
    try {
      const data = await updateMyProfile(
        {
          full_name: profile.full_name.trim(),
          email: profile.email.trim(),
          username: profile.username.trim() || null,
        },
        token
      );
      const normalized = normalizeProfile(data);
      setProfile(normalized);
      updateCurrentUser?.({
        full_name: normalized.full_name,
        email: normalized.email,
        username: normalized.username,
        photo_url: normalized.photo_url,
      });
      setSaveSuccess("Profile saved.");
    } catch (err) {
      setSaveError(messageForError(err?.message || "Unable to save your profile."));
    } finally {
      setSaving(false);
    }
  };

  const handleUploadProfilePhoto = async (file) => {
    if (!file || !token) return "";
    const data = await uploadMyProfilePhoto(file, token);
    const normalized = normalizeProfile(data);
    setProfile(normalized);
    updateCurrentUser?.({
      full_name: normalized.full_name,
      email: normalized.email,
      username: normalized.username,
      photo_url: normalized.photo_url,
    });
    setSaveSuccess("Profile picture saved.");
    return normalized.photo_url || "";
  };

  const handleRemoveProfilePhoto = async () => {
    if (!token) return "";
    const data = await removeMyProfilePhoto(token);
    const normalized = normalizeProfile(data);
    setProfile(normalized);
    updateCurrentUser?.({
      full_name: normalized.full_name,
      email: normalized.email,
      username: normalized.username,
      photo_url: normalized.photo_url,
    });
    setSaveSuccess("Profile picture removed.");
    return normalized.photo_url || "";
  };

  const handlePasswordSubmit = async (event) => {
    event.preventDefault();
    setPasswordError("");
    setPasswordSuccess("");
    if (!newPassword || newPassword.length < 8) {
      setPasswordError("New password must be at least 8 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError("New password and confirmation do not match.");
      return;
    }
    if (!token) {
      setPasswordError("You are no longer signed in.");
      return;
    }
    setChangingPassword(true);
    try {
      await changeMyPassword(
        {
          current_password: currentPassword,
          new_password: newPassword,
        },
        token
      );
      setPasswordSuccess("Password updated. Please sign in again.");
      setShowPasswordModal(true);
    } catch (err) {
      setPasswordError(messageForError(err?.message || "Could not change password."));
    } finally {
      setChangingPassword(false);
    }
  };

  const handlePasswordAcknowledge = async () => {
    setShowPasswordModal(false);
    await logout();
    navigate("/login");
  };

  return (
    <SalonBusinessCpaShell
      title="My profile"
      description="Manage your login details, change your password, and review the Salon IDs connected to your Ellyanna Accounting work."
    >
      {(saveError || saveSuccess || passwordError || passwordSuccess) ? (
        <div className="space-y-3">
          {saveError ? (
            <div className="rounded-3xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
              {saveError}
            </div>
          ) : null}
          {saveSuccess ? (
            <div className="rounded-3xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700">
              {saveSuccess}
            </div>
          ) : null}
          {passwordError ? (
            <div className="rounded-3xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
              {passwordError}
            </div>
          ) : null}
          {passwordSuccess && !showPasswordModal ? (
            <div className="rounded-3xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700">
              {passwordSuccess}
            </div>
          ) : null}
        </div>
      ) : null}

      <section className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_420px]">
        <form
          onSubmit={handleSaveProfile}
          className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm"
        >
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div>
              <h2 className="text-xl font-semibold text-slate-950">Account</h2>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                These details belong to your sign-in and are not tied to a single salon.
              </p>
            </div>
            <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-sm font-semibold text-slate-600">
              {roleLabel(profile.role || user?.role)}
            </span>
          </div>

          {loading ? (
            <div className="mt-6 text-sm text-slate-500">Loading your profile...</div>
          ) : (
            <>
              <div className="mt-6 max-w-xl">
                <ImageUploadField
                  label="Profile picture"
                  helper="Shown on your Ellyanna Accounting account menu."
                  recommended="Square image recommended"
                  value={profile.photo_url}
                  onChange={(next) =>
                    setProfile((current) => ({ ...current, photo_url: next || "" }))
                  }
                  onUpload={handleUploadProfilePhoto}
                  onRemove={handleRemoveProfilePhoto}
                  allowUrlPaste={false}
                  maxSizeMb={2}
                />
              </div>

              <div className="mt-6 grid gap-4 md:grid-cols-2">
                <label className="text-sm font-semibold text-slate-700">
                  Full name
                  <input
                    type="text"
                    value={profile.full_name}
                    onChange={(event) => handleFieldChange("full_name", event.target.value)}
                    className="mt-2 h-11 w-full rounded-2xl border border-slate-300 px-4 text-base font-normal text-slate-950 shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                    placeholder="Full name"
                  />
                </label>
                <label className="text-sm font-semibold text-slate-700">
                  Email
                  <input
                    type="email"
                    value={profile.email}
                    onChange={(event) => handleFieldChange("email", event.target.value)}
                    className="mt-2 h-11 w-full rounded-2xl border border-slate-300 px-4 text-base font-normal text-slate-950 shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                    placeholder="you@example.com"
                  />
                </label>
                <label className="text-sm font-semibold text-slate-700 md:col-span-2">
                  Login username
                  <input
                    type="text"
                    value={profile.username}
                    onChange={(event) => handleFieldChange("username", event.target.value)}
                    className="mt-2 h-11 w-full rounded-2xl border border-slate-300 px-4 text-base font-normal text-slate-950 shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                    placeholder="username"
                  />
                  <span className="mt-2 block text-sm font-normal leading-6 text-slate-500">
                    You can sign in with your email or this username.
                  </span>
                </label>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  type="submit"
                  disabled={saving}
                  className="rounded-full bg-slate-950 px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:opacity-70"
                >
                  {saving ? "Saving..." : "Save profile"}
                </button>
              </div>
            </>
          )}
        </form>

        <form
          onSubmit={handlePasswordSubmit}
          className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm"
        >
          <h2 className="text-xl font-semibold text-slate-950">Password</h2>
          <p className="mt-2 text-sm leading-6 text-slate-600">
            Change your password here. After it is saved, sign in again.
          </p>

          <div className="mt-6 space-y-4">
            <label className="block text-sm font-semibold text-slate-700">
              Current password
              <input
                type="password"
                value={currentPassword}
                onChange={(event) => setCurrentPassword(event.target.value)}
                className="mt-2 h-11 w-full rounded-2xl border border-slate-300 px-4 text-base font-normal text-slate-950 shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                required
              />
            </label>
            <label className="block text-sm font-semibold text-slate-700">
              New password
              <input
                type="password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                className="mt-2 h-11 w-full rounded-2xl border border-slate-300 px-4 text-base font-normal text-slate-950 shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                placeholder="At least 8 characters"
                required
              />
            </label>
            <label className="block text-sm font-semibold text-slate-700">
              Confirm new password
              <input
                type="password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                className="mt-2 h-11 w-full rounded-2xl border border-slate-300 px-4 text-base font-normal text-slate-950 shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                required
              />
            </label>
          </div>

          <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm leading-6 text-slate-600">
            Password changes apply to this Ellyanna login everywhere it is allowed.
          </div>

          <div className="mt-6 flex justify-end">
            <button
              type="submit"
              disabled={changingPassword}
              className="rounded-full bg-rose-500 px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-rose-600 disabled:opacity-70"
            >
              {changingPassword ? "Updating..." : "Change password"}
            </button>
          </div>
        </form>
      </section>

      {isAccountant ? (
        <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div>
              <h2 className="text-xl font-semibold text-slate-950">Assigned Salon IDs</h2>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                These are the salons you can help with in Ellyanna Accounting.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2 text-center sm:grid-cols-4">
              {[
                ["Open", totals.open],
                ["Assigned", totals.assignedOpen],
                ["Received", totals.received],
                ["Review", totals.reviewing],
              ].map(([label, value]) => (
                <div key={label} className="rounded-2xl bg-slate-50 px-4 py-3">
                  <div className="text-lg font-semibold text-slate-950">{value}</div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {assignmentsLoading ? (
            <div className="mt-6 text-sm text-slate-500">Loading assigned Salon IDs...</div>
          ) : assignments.length ? (
            <div className="mt-6 grid gap-4">
              {assignments.map((assignment) => (
                <article
                  key={assignment.id}
                  className="rounded-3xl border border-slate-200 bg-slate-50 p-5"
                >
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-lg font-semibold text-slate-950">
                          {assignment.salon_name || `Salon ID ${assignment.salon_id}`}
                        </h3>
                        <span className="rounded-full border border-slate-300 bg-white px-3 py-1 text-sm font-semibold text-slate-700">
                          Salon ID {assignment.salon_id}
                        </span>
                        <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-sm font-semibold text-emerald-700">
                          {accessLabel(assignment.access_level)}
                        </span>
                      </div>
                      <p className="mt-2 text-sm leading-6 text-slate-600">
                        {assignment.business_name || `Business ID ${assignment.business_id}`}
                      </p>
                      <p className="mt-1 text-sm text-slate-500">
                        Assigned {formatDateTime(assignment.assigned_at_utc)}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Link
                        to={accountingPath("/client-summary", {
                          business_id: assignment.business_id,
                          salon_id: assignment.salon_id,
                        })}
                        className="rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100"
                      >
                        Client summary
                      </Link>
                      <Link
                        to={accountingPath("/review", {
                          business_id: assignment.business_id,
                          salon_id: assignment.salon_id,
                        })}
                        className="rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100"
                      >
                        Open review
                      </Link>
                      <Link
                        to={accountingPath("/data", {
                          business_id: assignment.business_id,
                          salon_id: assignment.salon_id,
                        })}
                        className="rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100"
                      >
                        Source data
                      </Link>
                    </div>
                  </div>
                  <div className="mt-5 grid gap-3 sm:grid-cols-4">
                    {[
                      ["Open requests", assignment.open_request_count],
                      ["Assigned open", assignment.assigned_open_request_count],
                      ["Received", assignment.received_document_count],
                      ["Being reviewed", assignment.reviewing_document_count],
                    ].map(([label, value]) => (
                      <div key={label} className="rounded-2xl bg-white px-4 py-3">
                        <div className="text-lg font-semibold text-slate-950">
                          {Number(value || 0)}
                        </div>
                        <div className="text-sm text-slate-500">{label}</div>
                      </div>
                    ))}
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="mt-6 rounded-3xl border border-amber-200 bg-amber-50 p-5 text-sm leading-6 text-amber-800">
              No Salon IDs are assigned to this accountant account yet.
            </div>
          )}
        </section>
      ) : null}

      {isAccountant ? (
        <section className="rounded-3xl border border-slate-200 bg-white p-5 text-sm leading-6 text-slate-600 shadow-sm">
          This account is for Ellyanna Accounting review work only. It does not give
          access to Ellyanna Booking or Ellyanna Boothshare.
        </section>
      ) : null}

      <ConfirmModal
        isOpen={showPasswordModal}
        title="Password updated"
        message="Your password has been updated. Please sign in again."
        confirmText="OK"
        hideCancel
        showClose={false}
        onConfirm={handlePasswordAcknowledge}
        onClose={handlePasswordAcknowledge}
      />
    </SalonBusinessCpaShell>
  );
}
